import { useState, useEffect } from 'react';
import { Trash2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogBody,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import type { TodoListResponse, TodoProjectResponse, TodoListLinkResponse } from '@/lib/api';
import { sanitizeKeyInput, LIST_KEY_MAX } from '../lib/itemId';
import { ProjectsManager } from './ProjectsManager';
import { LinkedListsManager } from './LinkedListsManager';

// Mirror VAULT_TODO_LIST_NAME_MAX / VAULT_TODO_LIST_DESCRIPTION_MAX in
// @fias/db-types — the client deliberately does not depend on that package,
// so the caps are duplicated here (the server re-validates the real bounds
// on every write).
const LIST_NAME_MAX = 200;
const LIST_DESCRIPTION_MAX = 5000;

interface ListSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  list: TodoListResponse;
  onSave: (input: {
    name?: string;
    description?: string | null;
    itemKey?: string;
    isPublic?: boolean;
    publicSlug?: string;
    reviewMode?: boolean;
    showProjectOnTasks?: boolean;
    showSectionPathOnTasks?: boolean;
  }) => Promise<void> | void;
  onDelete: () => Promise<void> | void;
  /**
   * Workspace members the owner/admin can designate as reviewers. Empty/omitted
   * on Personal lists — the Reviewers section is hidden there.
   */
  assignableMembers?: Array<{ principalId: string; label: string }>;
  /** Principals currently designated as reviewers of this list. */
  reviewerPrincipalIds?: Set<string>;
  /** Persist the reviewer set (separate endpoint from the list fields). */
  onSaveReviewers?: (principalIds: string[]) => Promise<void>;
  /** Workspace projects + their CRUD handlers. Present for managers only; the
   *  Projects manager section is hidden when omitted. Mutations apply immediately
   *  (independent of the form's Save). */
  projects?: TodoProjectResponse[];
  onCreateProject?: (input: { name: string; color?: string | null }) => Promise<unknown>;
  onUpdateProject?: (
    projectId: string,
    input: { name?: string; color?: string | null },
  ) => Promise<unknown>;
  onDeleteProject?: (projectId: string) => Promise<unknown>;
  /** Cross-list move shortcuts for THIS list + the other workspace lists to link
   *  to. Present for managers only; the Linked lists section is hidden when
   *  omitted. Mutations apply immediately (independent of the form's Save). */
  links?: TodoListLinkResponse[];
  otherLists?: Array<{ listId: string; name: string }>;
  onCreateLink?: (targetListId: string) => Promise<unknown>;
  onDeleteLink?: (linkId: string) => Promise<unknown>;
}

function sameMembers(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

export function ListSettingsDialog({
  open,
  onClose,
  list,
  onSave,
  onDelete,
  assignableMembers,
  reviewerPrincipalIds,
  onSaveReviewers,
  projects,
  onCreateProject,
  onUpdateProject,
  onDeleteProject,
  links,
  otherLists,
  onCreateLink,
  onDeleteLink,
}: ListSettingsDialogProps) {
  const showReviewers = !!assignableMembers && assignableMembers.length > 0 && !!onSaveReviewers;
  const showProjects = !!onCreateProject && !!onUpdateProject && !!onDeleteProject;
  const showLinks = !!onCreateLink && !!onDeleteLink;
  const [name, setName] = useState(list.name);
  const [description, setDescription] = useState(list.description ?? '');
  const [itemKey, setItemKey] = useState(list.itemKey);
  const [isPublic, setIsPublic] = useState(list.isPublic);
  const [publicSlug, setPublicSlug] = useState(list.publicSlug ?? '');
  const [reviewMode, setReviewMode] = useState(list.reviewMode);
  const [showProjectOnTasks, setShowProjectOnTasks] = useState(list.showProjectOnTasks);
  const [showSectionPathOnTasks, setShowSectionPathOnTasks] = useState(list.showSectionPathOnTasks);
  const [reviewers, setReviewers] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(list.name);
      setDescription(list.description ?? '');
      setItemKey(list.itemKey);
      setIsPublic(list.isPublic);
      setPublicSlug(list.publicSlug ?? '');
      setReviewMode(list.reviewMode);
      setShowProjectOnTasks(list.showProjectOnTasks);
      setShowSectionPathOnTasks(list.showSectionPathOnTasks);
      setReviewers(new Set(reviewerPrincipalIds ?? []));
      setSaveError(null);
      setDeleteError(null);
    }
  }, [open, list, reviewerPrincipalIds]);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      onClose();
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setSaveError('Name cannot be empty');
      return;
    }
    if (!itemKey) {
      setSaveError('Task ID prefix cannot be empty');
      return;
    }
    // description is nullable server-side: send `null` to clear it, the new
    // text when set, and `undefined` when unchanged (avoids a no-op write).
    const trimmedDescription = description.trim();
    const descriptionChanged = trimmedDescription !== (list.description ?? '');
    setSaving(true);
    setSaveError(null);
    try {
      await onSave({
        // Only send fields that actually changed to avoid a no-op write.
        name: trimmedName !== list.name ? trimmedName : undefined,
        description: descriptionChanged ? trimmedDescription || null : undefined,
        itemKey: itemKey !== list.itemKey ? itemKey : undefined,
        isPublic,
        publicSlug: isPublic ? publicSlug.trim() || undefined : undefined,
        reviewMode,
        showProjectOnTasks,
        showSectionPathOnTasks,
      });
      // Reviewers persist through a separate endpoint; only write when changed.
      if (
        showReviewers &&
        onSaveReviewers &&
        !sameMembers(reviewers, new Set(reviewerPrincipalIds ?? []))
      ) {
        await onSaveReviewers(Array.from(reviewers));
      }
      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleConfirmDelete() {
    setDeleting(true);
    setDeleteError(null);
    try {
      await onDelete();
      setDeleteConfirmOpen(false);
      setDeleteConfirmText('');
      onClose();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90dvh] max-w-md flex flex-col">
        <DialogHeader>
          <DialogTitle>List Settings</DialogTitle>
          <DialogDescription>Configure visibility and sharing for this list.</DialogDescription>
        </DialogHeader>

        <DialogBody>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="list-name">Name</Label>
              <Input
                id="list-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Todo List"
                maxLength={LIST_NAME_MAX}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="list-description">Description (optional)</Label>
              <Textarea
                id="list-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What is this list for?"
                rows={3}
                maxLength={LIST_DESCRIPTION_MAX}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="list-key">Task ID prefix</Label>
              <Input
                id="list-key"
                value={itemKey}
                onChange={(e) => setItemKey(sanitizeKeyInput(e.target.value))}
                placeholder="FR"
                maxLength={LIST_KEY_MAX}
                className="font-mono uppercase w-28"
              />
              <p className="text-xs text-muted-foreground">
                Tasks show as{' '}
                <code className="rounded bg-muted px-1 py-0.5">{(itemKey || 'FR') + '-1'}</code>.
                Changing it re-labels existing tasks (their numbers stay).
              </p>
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <Label htmlFor="review-toggle">Require review before done</Label>
                <p className="text-xs text-muted-foreground">
                  Tasks can&rsquo;t be checked straight to done. The checkbox marks progress; when
                  ready, use &ldquo;Send for review&rdquo; to put a task In Review, and a reviewer
                  verifies it before it counts as completed.
                </p>
              </div>
              <Switch id="review-toggle" checked={reviewMode} onCheckedChange={setReviewMode} />
            </div>

            {showReviewers && (
              <div className="space-y-2">
                <Label>Reviewers</Label>
                <p className="text-xs text-muted-foreground">
                  Reviewers are notified when a task enters &ldquo;In Review&rdquo; and can verify
                  or send it back — even if it isn&rsquo;t assigned to them.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {assignableMembers!.map((m) => {
                    const selected = reviewers.has(m.principalId);
                    return (
                      <button
                        key={m.principalId}
                        type="button"
                        onClick={() =>
                          setReviewers((prev) => {
                            const next = new Set(prev);
                            if (next.has(m.principalId)) next.delete(m.principalId);
                            else next.add(m.principalId);
                            return next;
                          })
                        }
                        aria-pressed={selected}
                        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                          selected
                            ? 'border-amber-500/60 bg-amber-500/15 text-amber-700 dark:text-amber-300'
                            : 'border-border bg-transparent text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        {selected && <Check className="h-3 w-3" />}
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {showProjects && (
              <ProjectsManager
                projects={projects ?? []}
                onCreate={onCreateProject!}
                onUpdate={onUpdateProject!}
                onDelete={onDeleteProject!}
              />
            )}

            {showLinks && (
              <LinkedListsManager
                links={links ?? []}
                otherLists={otherLists ?? []}
                onCreate={onCreateLink!}
                onDelete={onDeleteLink!}
              />
            )}

            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <Label htmlFor="show-project-toggle">Show project on tasks</Label>
                <p className="text-xs text-muted-foreground">
                  Show each task&rsquo;s project as a chip in the section view.
                </p>
              </div>
              <Switch
                id="show-project-toggle"
                checked={showProjectOnTasks}
                onCheckedChange={setShowProjectOnTasks}
              />
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <Label htmlFor="show-section-path-toggle">Show section path on tasks</Label>
                <p className="text-xs text-muted-foreground">
                  Show which section a task lives in when grouping by project.
                </p>
              </div>
              <Switch
                id="show-section-path-toggle"
                checked={showSectionPathOnTasks}
                onCheckedChange={setShowSectionPathOnTasks}
              />
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <Label htmlFor="public-toggle">Make Public</Label>
                <p className="text-xs text-muted-foreground">
                  Allow anyone with the link to view this list
                </p>
              </div>
              <Switch id="public-toggle" checked={isPublic} onCheckedChange={setIsPublic} />
            </div>

            {isPublic && (
              <div className="space-y-2">
                <Label htmlFor="public-slug">Public Slug</Label>
                <Input
                  id="public-slug"
                  value={publicSlug}
                  onChange={(e) => setPublicSlug(e.target.value.toLowerCase())}
                  placeholder="my-todo-list"
                />
                {publicSlug.trim() && (
                  <p className="text-xs text-muted-foreground">
                    Preview:{' '}
                    <code className="rounded bg-muted px-1 py-0.5">
                      /a/arche_todo_lists/public/{publicSlug.trim()}
                    </code>
                  </p>
                )}
              </div>
            )}

            {saveError && <p className="text-sm text-destructive">{saveError}</p>}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving || !name.trim()}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>

          <div className="mt-6 border-t pt-4 space-y-2">
            <Label className="text-destructive">Danger zone</Label>
            <p className="text-xs text-muted-foreground">
              Deleting this list permanently removes all sections, items, and sharing settings.
            </p>
            <AlertDialog
              open={deleteConfirmOpen}
              onOpenChange={(nextOpen) => {
                setDeleteConfirmOpen(nextOpen);
                if (!nextOpen) {
                  setDeleteConfirmText('');
                  setDeleteError(null);
                }
              }}
            >
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete list
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this list?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete <span className="font-semibold">{list.name}</span>{' '}
                    and all of its sections and items. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="py-2">
                  <p className="text-sm text-muted-foreground mb-2">
                    Type <span className="font-mono font-semibold">delete</span> to confirm:
                  </p>
                  <Input
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="delete"
                    autoFocus
                  />
                  {deleteError && <p className="text-sm text-destructive mt-2">{deleteError}</p>}
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    disabled={deleting || deleteConfirmText.trim().toLowerCase() !== 'delete'}
                    onClick={(e) => {
                      e.preventDefault();
                      handleConfirmDelete();
                    }}
                  >
                    {deleting ? 'Deleting...' : 'Delete'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
